import { Index, type JSX } from 'solid-js';
import { AGENT_CARD_ORDER } from '../lib/agents';
import { AgentCardFrame, AgentGridFrame, AgentsStripFrame, DefaultCardFrame } from './AgentsFrames';
import { Skeleton } from './Skeleton';

// The Agents page's loading state (#5627, epic #5620 slice 06): the page's own layout with
// a `Skeleton` bar where each region's content will be. It renders through the SAME
// frames the loaded page does (`AgentsFrames`), so the strip, the full-width Default card,
// the two-column grid and one five-region agent card per `AGENT_CARD_ORDER` entry are all
// already in place when the data lands, and nothing but the content moves.
//
// ONE frame per `AGENT_CARD_ORDER` entry, so the skeleton draws exactly as many
// cards as the loaded grid does: the e2e geometry gates wait on that exact
// count (`AGENT_GRID_CARD_COUNT`), and a card added to the grid without its
// skeleton frame would fail them.
//
// The bars are sized in CSS from the region floors (`views/_agents.scss`): each region's
// `min-block-size` is its shortest real case, so a bar and the content that replaces it
// fill the same track. A region whose content turns out taller grows downward; nothing
// above it moves.
//
// Decorative: every frame is `aria-hidden` and the root says `aria-busy`. The announcement
// that the page is loading is the live region `QueryBoundary` wraps around this, not a
// screen-reader walk through forty empty boxes. Nothing in here is focusable and nothing
// is a heading. No frame sets `data-agent`; a skeleton card is not an agent.

export function AgentsSkeleton(): JSX.Element {
  return (
    <div class="agents__skeleton" aria-busy="true">
      <AgentsStripFrame decorative keys={<Skeleton />} running={<Skeleton />} />

      <DefaultCardFrame
        decorative
        head={<Skeleton />}
        fields={<Skeleton />}
        repo={
          <section class="agent-drawer__section" aria-hidden="true">
            <Skeleton />
          </section>
        }
      />

      <AgentGridFrame decorative>
        <Index each={AGENT_CARD_ORDER}>
          {() => (
            <AgentCardFrame
              decorative
              head={<Skeleton />}
              job={<Skeleton />}
              model={<Skeleton />}
              stats={<Skeleton />}
              foot={<Skeleton />}
            />
          )}
        </Index>
      </AgentGridFrame>
    </div>
  );
}
